import { Inject, Injectable, InjectionToken } from '@angular/core';
import { StorageService } from './storage.service';
import { requestIdleOrTimeout } from '../utils/idle-helper';

export const BROWSER_STORAGE = new InjectionToken<Storage>('Browser Storage', {
  providedIn: 'root',
  factory: () => localStorage
});

@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {
  private migrationAttempted = false;
  private isIndexedDBAvailable = false;

  constructor(
    @Inject(BROWSER_STORAGE) public storage: Storage,
    private storageService: StorageService
  ) {
    // RQ-04: Defer migration to idle time (constructor must be < 5ms)
    requestIdleOrTimeout(() => this.initMigration(), 3000);
  }

  /**
   * Initialize migration from localStorage to IndexedDB
   */
  private async initMigration() {
    if (this.migrationAttempted) return;
    
    this.migrationAttempted = true;
    try {
      this.isIndexedDBAvailable = await this.storageService.isAvailable();
      if (this.isIndexedDBAvailable) {
        await this.storageService.migrateFromLocalStorage();
        // console.log('Storage migration completed successfully');
      } else {
        console.warn('IndexedDB not available, continuing with localStorage');
      }
    } catch (error) {
      console.warn('Migration from localStorage failed:', error);
      this.isIndexedDBAvailable = false;
    }
  }

  /**
   * Get an item from storage
   * @param key - The key to retrieve
   * @returns The value or null if not found
   */
  get(key: string): string | null {
    try {
      return this.storage.getItem(key);
    } catch (error) {
      console.error(`Error getting item "${key}" from localStorage:`, error);
      return null;
    }
  }

  /**
   * Get an item from storage (async version) - uses IndexedDB when available
   * @param key - The key to retrieve
   * @returns Promise that resolves to the value or null if not found
   */
  async getAsync<T>(key: string): Promise<T | null> {
    try {
      if (this.isIndexedDBAvailable) {
        return await this.storageService.getItem<T>(key);
      } else {
        const value = this.storage.getItem(key);
        return value ? JSON.parse(value) : null;
      }
    } catch (error) {
      console.error(`Error getting item "${key}" from storage:`, error);
      return null;
    }
  }

  /**
   * Set an item in storage
   * @param key - The key to store the value under
   * @param value - The value to store
   */
  set(key: string, value: string): void {
    try {
      this.storage.setItem(key, value);
      // Also store in IndexedDB for future use (non-blocking)
      if (this.isIndexedDBAvailable) {
        this.storageService.setItem(key, value).catch(error => {
          console.warn('Failed to store in IndexedDB:', error);
        });
      }
    } catch (error) {
      console.error(`Error setting item "${key}" in localStorage:`, error);
      // If localStorage fails, try IndexedDB as fallback
      if (this.isIndexedDBAvailable) {
        this.storageService.setItem(key, value).catch(fallbackError => {
          console.error('Both localStorage and IndexedDB failed:', fallbackError);
          // Throw error for critical survey data
          if (key.includes('construct-') || key.includes('form-fields-')) {
            throw new Error(`Critical storage failure for ${key}: ${error.message}`);
          }
        });
      } else {
        // For critical survey data, throw an error to prevent data loss
        if (key.includes('construct-') || key.includes('form-fields-')) {
          throw new Error(`Critical storage failure for ${key}: ${error.message}`);
        }
      }
    }
  }

  /**
   * Set an item in storage (async version) - prioritizes IndexedDB
   * @param key - The key to store the value under
   * @param value - The value to store
   * @param ttlInMs - Optional time-to-live in milliseconds
   */
  async setAsync<T>(key: string, value: T, ttlInMs?: number): Promise<void> {
    try {
      if (this.isIndexedDBAvailable) {
        await this.storageService.setItem(key, value, ttlInMs);
      } else {
        this.storage.setItem(key, JSON.stringify(value));
      }
    } catch (error) {
      console.error(`Error setting item "${key}" in storage:`, error);
      // For critical survey data, throw an error to prevent data loss
      if (key.includes('construct-') || key.includes('form-fields-')) {
        throw new Error(`Critical storage failure for ${key}: ${error.message}`);
      }
    }
  }

  /**
   * Remove an item from storage
   * @param key - The key to remove
   */
  remove(key: string): void {
    try {
      this.storage.removeItem(key);
      // Also remove from IndexedDB (non-blocking)
      if (this.isIndexedDBAvailable) {
        this.storageService.removeItem(key).catch(error => {
          console.warn('Failed to remove from IndexedDB:', error);
        });
      }
    } catch (error) {
      console.error(`Error removing item "${key}" from localStorage:`, error);
    }
  }

  /**
   * Remove an item from storage (async version)
   * @param key - The key to remove
   */
  async removeAsync(key: string): Promise<void> {
    try {
      if (this.isIndexedDBAvailable) {
        await this.storageService.removeItem(key);
      } else {
        this.storage.removeItem(key);
      }
    } catch (error) {
      console.error(`Error removing item "${key}" from storage:`, error);
    }
  }

  /**
   * Clear all items from storage
   */
  clear(): void {
    try {
      this.storage.clear();
      // Also clear IndexedDB (non-blocking)
      if (this.isIndexedDBAvailable) {
        this.storageService.clear().catch(error => {
          console.warn('Failed to clear IndexedDB:', error);
        });
      }
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  }

  /**
   * Clear all items from storage (async version)
   */
  async clearAsync(): Promise<void> {
    try {
      if (this.isIndexedDBAvailable) {
        await this.storageService.clear();
      } else {
        this.storage.clear();
      }
    } catch (error) {
      console.error('Error clearing storage:', error);
    }
  }

  /**
   * Check available storage space (localStorage only)
   */
  getStorageSpaceInfo(): { available: boolean; usedSpace?: number; error?: string } {
    try {
      // Test if we can write to localStorage
      const testKey = '_storage_test_' + Date.now();
      const testValue = 'test';
      this.storage.setItem(testKey, testValue);
      this.storage.removeItem(testKey);
      
      // Estimate used space
      let usedSpace = 0;
      for (let key in this.storage) {
        if (this.storage.hasOwnProperty(key)) {
          usedSpace += this.storage[key].length + key.length;
        }
      }
      
      return { available: true, usedSpace };
    } catch (error) {
      return { 
        available: false, 
        error: error.message,
        usedSpace: 0
      };
    }
  }

  /**
   * Force migration to IndexedDB for critical data
   */
  async migrateCriticalData(): Promise<void> {
    if (!this.isIndexedDBAvailable) {
      console.warn('IndexedDB not available for critical data migration');
      return;
    }

    const criticalKeys = [];
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      if (key && (key.includes('construct-') || key.includes('form-fields-') || key.includes('qservus-surv-user-data'))) {
        criticalKeys.push(key);
      }
    }

    for (const key of criticalKeys) {
      try {
        const value = this.storage.getItem(key);
        if (value) {
          await this.storageService.setItem(key, value);
        }
      } catch (error) {
        console.error(`Failed to migrate critical key "${key}":`, error);
      }
    }
  }

  /**
   * Get storage status information
   */
  async getStorageStatus(): Promise<{
    localStorageAvailable: boolean;
    indexedDBAvailable: boolean;
    localStorageSpace: any;
    indexedDBStats?: any;
  }> {
    const localStorageSpace = this.getStorageSpaceInfo();
    let indexedDBStats;

    if (this.isIndexedDBAvailable) {
      try {
        indexedDBStats = await this.storageService.getStorageStats();
      } catch (error) {
        console.warn('Failed to get IndexedDB stats:', error);
      }
    }

    return {
      localStorageAvailable: localStorageSpace.available,
      indexedDBAvailable: this.isIndexedDBAvailable,
      localStorageSpace,
      indexedDBStats
    };
  }
}
